// Web-only React Flow renderer for the read-only viewer.
//
// Split into its own module (loaded via require() from FlowchartViewer on the
// web branch only) so the native bundler never *executes* React Flow's
// DOM-only internals — the same deferral the original inline require() gave us.
//
// IMPORTANT: import @xyflow/react and ./nodes via ESM here — the SAME way
// FlowchartDesigner and ./nodes do. Mixing require('@xyflow/react') (CJS entry)
// with the ESM import used in ./nodes loads TWO copies of the library (the
// dual-package hazard): the custom-node <Handle>s bind to one store context
// while <ReactFlow>/<ReactFlowProvider> create another, so React Flow throws
// error #001 ("Seems like you have not used zustand provider as an ancestor"),
// which blanks the entire Detail screen for any item that has a saved flowchart.
import React, { useMemo } from 'react';
import { View } from 'react-native';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FLOWCHART_NODE_TYPES_MAP } from './nodes';
import { type FlowchartData } from './types';

export default function FlowchartWebViewer({
  value,
  height,
}: {
  value: FlowchartData;
  height: number;
}) {
  const nodes = useMemo(
    () =>
      value.nodes.map((n) => ({
        id: n.id,
        type: n.type,
        position: n.position || { x: 0, y: 0 },
        data: { label: n.label, readOnly: true },
        draggable: false,
        selectable: false,
      })),
    [value.nodes]
  );

  const edges = useMemo(
    () =>
      value.edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        label: e.label,
        markerEnd: { type: MarkerType.ArrowClosed },
      })),
    [value.edges]
  );

  return (
    <View style={{ height, borderRadius: 8, overflow: 'hidden', borderWidth: 1, borderColor: '#E2E8F0', backgroundColor: '#FAFAFA' }}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={FLOWCHART_NODE_TYPES_MAP}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag
          zoomOnScroll
        >
          <Background gap={20} size={1} color="#CBD5E1" />
          <Controls position="bottom-left" showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>
    </View>
  );
}
